import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import type { ReceiveConvoyConfirmResultDto, ReceiveConvoyReceiptDto } from '@/entities/receive';
import { ConfirmArrivalWarnings } from '@/features/receive-confirm-arrival';
import { formatDisplayDate } from '@/shared/lib';
import { DataTable, type DataTableColumn, type DataTableRow } from '@/shared/ui/data-table';

type ReceiptRow = ReceiveConvoyReceiptDto & DataTableRow;

interface ReceiveConvoyResultProps {
  result: ReceiveConvoyConfirmResultDto;
}

/**
 * What the convoy receipt wrote: one receipt per truck and receive location,
 * each with its GRN number. The numbers exist nowhere else — there is no print
 * endpoint yet — so they are listed here in full.
 */
export function ReceiveConvoyResult({ result }: ReceiveConvoyResultProps) {
  const { t } = useTranslation();
  const nothingWritten = result.receipts.length === 0;

  const receiptRows = useMemo<ReceiptRow[]>(
    () => result.receipts.map((receipt) => ({ ...receipt, id: receipt.receiveShipmentId })),
    [result.receipts]
  );

  const columns = useMemo<DataTableColumn<ReceiptRow>[]>(
    () => [
      {
        id: 'waybill',
        header: t('waybill.title.label', 'Waybill'),
        cell: (row) => <span className="font-mono">{row.waybill || '—'}</span>
      },
      {
        id: 'goodsReceivedNoteNumber',
        header: t('grn.number.label', 'Goods Received Note Number'),
        cell: (row) => (
          <Badge variant="outline" className="font-mono">
            {row.goodsReceivedNoteNumber}
          </Badge>
        )
      },
      {
        id: 'receivedAtLocation',
        header: t('receive.receivelocation.label', 'Receive Location'),
        cell: (row) => row.receivedAtLocation
      },
      {
        id: 'processedLines',
        header: t('receive.convoy.result.lines', 'Lines'),
        width: 'w-24',
        className: 'text-right tabular-nums',
        cell: (row) => row.processedLines
      }
    ],
    [t]
  );

  return (
    <div className="space-y-4">
      <Alert variant={nothingWritten ? 'info' : 'success'}>
        <CheckCircle2 />
        <AlertTitle>
          {nothingWritten
            ? t('receive.confirm.result.nothingReceived', 'No quantities were recorded')
            : t('receive.confirm.result.title', 'Receipt recorded')}
        </AlertTitle>
        <AlertDescription>
          {nothingWritten
            ? t(
                'receive.convoy.result.nothingReceivedDesc',
                'Every line was zero, so nothing was written. The convoy is still awaiting receipt.'
              )
            : t(
                'receive.convoy.result.summary',
                '{{processed}} line(s) recorded in {{receipts}} receipt(s) on {{date}}',
                {
                  processed: result.processedLines,
                  receipts: result.receipts.length,
                  date: formatDisplayDate(result.receiveDate)
                }
              )}
        </AlertDescription>
      </Alert>

      {!nothingWritten && (
        <div className="space-y-1">
          <DataTable
            data={receiptRows}
            columns={columns}
            tableId="receive-convoy-receipts"
            showToolbar={false}
            enableColumnCustomization={false}
            enableFullscreen={false}
            height="h-auto"
            contentClassName="px-0"
          />
          {result.printGoodsReceivedNote && (
            <p className="text-xs text-muted-foreground">
              {t(
                'receive.confirm.result.printPending',
                'Printing the Goods Received Note is not available yet — note the number above.'
              )}
            </p>
          )}
        </div>
      )}

      {result.skippedLines > 0 && (
        <p className="text-sm text-muted-foreground">
          {t('receive.confirm.result.skipped', '{{count}} line(s) skipped because every quantity was zero.', {
            count: result.skippedLines
          })}
        </p>
      )}

      {result.consignmentStatusChanges.length > 0 && (
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            {t('receive.confirm.result.statusChanges', 'Consignment status changes')}
          </p>
          <ul className="space-y-1 text-sm text-muted-foreground">
            {result.consignmentStatusChanges.map((change) => (
              <li key={change.consignmentId}>
                {change.consignmentNumber}: {change.fromStatusCode} → {change.toStatusCode}
              </li>
            ))}
          </ul>
        </div>
      )}

      <ConfirmArrivalWarnings warnings={result.warnings} />
    </div>
  );
}
